/**
 * The dashboard's single React Query client, and the two global error handlers
 * hanging off it.
 *
 * Extracted from `App.tsx` so the handlers can be exercised by a test with a
 * REAL `QueryClient` and a real failing query. While they lived inside a
 * component's `useMemo`, the only way to reach them was to render the whole app
 * — which is why the mutation handler has had no wiring test since it was
 * written, and why the missing QUERY handler (see `queryErrorReporting.ts`) went
 * unnoticed for months.
 *
 * Behaviour of the mutation path is unchanged by the extraction.
 */
import * as Sentry from '@sentry/react';
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query';
import { classifyApiError } from './utils/apiErrorReporting';
import { isBrowserOnline } from './utils/browserOnline';
import { shouldReportToSentry } from './utils/expectedErrors';
import { classifyQueryFailure } from './utils/queryErrorReporting';

/** Just enough of Chakra's `useToast` return value for this module. */
export type ToastFn = (options: {
    title: string;
    description?: string;
    status: 'error';
    duration: number;
}) => unknown;

/*
 * The `navigator.onLine` read that used to live here as a private `isOnline()`
 * is now `utils/browserOnline.ts`, shared with `features/_shared/readState.ts`.
 * Two decisions turn on the same signal and a second copy is a fork (D59).
 */

export function createDashboardQueryClient(toast: ToastFn): QueryClient {
    return new QueryClient({
        // Failed READS. Until this existed, a query that failed permanently was
        // invisible to Sentry — no capture, no breadcrumb. See
        // `utils/queryErrorReporting.ts` for what is reported and what is not.
        queryCache: new QueryCache({
            onError: (error) => {
                const report = classifyQueryFailure(error, { online: isBrowserOnline() });
                if (report.kind === 'breadcrumb') {
                    Sentry.addBreadcrumb(report.breadcrumb);
                } else if (report.kind === 'capture') {
                    Sentry.captureException(error, report.context);
                }
                // No toast: a query refetches on mount, focus and reconnect
                // without the operator asking, and every page renders its own
                // error state.
            },
        }),
        mutationCache: new MutationCache({
            onError: (error) => {
                // A mistyped password is not a defect. This handler sees EVERY
                // failed mutation, so without the guard an ordinary failed login
                // is filed as a Sentry error — DASHBOARD-1Q / -Y / -3S. The
                // toast below still fires either way, so the user is told
                // regardless.
                //
                // DASHBOARD-2: an ApiError additionally needs classifying (an
                // API refusal the operator was shown is an outcome, not a fault)
                // and fingerprinting by route (every ApiError shares one stack
                // frame, so 13 unrelated failures grouped into one untriageable
                // issue). See utils/apiErrorReporting.ts.
                const apiReport = classifyApiError(error);
                if (apiReport?.kind === 'expected') {
                    Sentry.addBreadcrumb(apiReport.breadcrumb);
                } else if (shouldReportToSentry(error)) {
                    Sentry.captureException(error, apiReport?.context);
                }
                toast({
                    title: 'An error occurred',
                    description: (error as { message?: string })?.message,
                    status: 'error',
                    duration: 5000,
                });
            },
        }),
    });
}
